import { useMemo, useEffect } from 'react';
import { trackReportProductionDatesToggled } from '@/lib/analytics';
import { format } from 'date-fns';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import { UnitModelFilter } from '@/components/layout/UnitModelFilter';
import { TimePeriodSelector } from './TimePeriodSelector';
import { MetricChipSelector } from './MetricChipSelector';
import { StatusBreakdown, ModelBreakdown } from './ReportSummaryCards';
import { useFilters } from '@/contexts/FilterContext';
import { cn } from '@/lib/utils';
import type { UnitStatusFocus } from '@/hooks/useReportFocus';
import type {
  ReportFilters,
  ReportSummary,
  GroupAnalyticsFilters,
  TimeGranularity,
  MetricKey,
  DatePreset,
} from '@/types/reports';
import type { Group, Company, IndustryType } from '@/types';

interface ReportsFiltersSidebarProps {
  filters: ReportFilters;
  onFiltersChange: (filters: ReportFilters) => void;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  summary?: ReportSummary;
  groups?: Group[];
  companies?: Company[];
  analyticsFilters?: GroupAnalyticsFilters;
  onAnalyticsFiltersChange?: (filters: GroupAnalyticsFilters) => void;
  /** Industry types from FilterContext Industry filter - used to filter model dropdown */
  selectedIndustryTypes?: IndustryType[];
  /** Whether to show the onboarding tooltip */
  showOnboarding?: boolean;
  /** Callback to dismiss onboarding tooltip */
  onDismissOnboarding?: () => void;
  /** Whether to stretch the sidebar to fill available height (for empty state alignment) */
  stretchToFill?: boolean;
  /** Model the report is focused on via Units by Model (click-to-focus) */
  focusedModel?: string | null;
  /** Click on a Units by Model row: focus the report on it (again to clear) */
  onToggleModelFocus?: (modelCode: string) => void;
  onClearModelFocus?: () => void;
  /** Status the report is focused on via Unit Status (click-to-focus) */
  focusedStatus?: UnitStatusFocus | null;
  onToggleStatusFocus?: (status: UnitStatusFocus) => void;
  onClearStatusFocus?: () => void;
}

export function ReportsFiltersSidebar({
  filters,
  onFiltersChange,
  onResetFilters,
  hasActiveFilters,
  summary,
  groups = [],
  companies = [],
  analyticsFilters,
  onAnalyticsFiltersChange,
  selectedIndustryTypes: _industryTypesFromContext = [],
  showOnboarding = false,
  onDismissOnboarding,
  stretchToFill = false,
  focusedModel = null,
  onToggleModelFocus,
  onClearModelFocus,
  focusedStatus = null,
  onToggleStatusFocus,
  onClearStatusFocus,
}: ReportsFiltersSidebarProps) {
  // Get the selected industry types from the main FilterContext (header filters)
  // This ensures the sidebar ModelSelector shows the same models as the header UnitModelFilter
  const { selectedIndustryTypes: mainFilterIndustryTypes } = useFilters();

  // Determine the industries from selected groups/companies (for group-specific logic)
  const groupBasedIndustryTypes: IndustryType[] = (() => {
    if (!analyticsFilters?.groupIds.length) return [];
    const selectedGroups = groups.filter(g => analyticsFilters.groupIds.includes(g.id));
    const companyIds = [...new Set(selectedGroups.map(g => g.company_id))];
    // Get all unique industries from matching companies
    const industries = companies
      .filter(c => companyIds.includes(c.id) && c.industry)
      .map(c => c.industry as IndustryType);
    return [...new Set(industries)];
  })();

  // Determine if selected groups are film industry (for group dates checkbox visibility)
  // When groups are explicitly selected, use their industry; otherwise fall back to header filter
  const hasSelectedGroups = analyticsFilters?.groupIds && analyticsFilters.groupIds.length > 0;
  const isFilmIndustry = hasSelectedGroups
    ? groupBasedIndustryTypes.includes('film')  // Only show if selected groups include film
    : mainFilterIndustryTypes.includes('film'); // Fall back to header filter when nothing selected

  // Get all selected root groups (productions) — supports multi-selection
  const selectedRootGroups = useMemo(() => {
    if (!analyticsFilters?.groupIds.length) return [];
    const selectedIds = new Set(analyticsFilters.groupIds);
    // Find root groups directly selected or whose children are selected
    const rootProductions = groups.filter(g => !g.parent_group_id);
    return rootProductions.filter(root => {
      if (selectedIds.has(root.id)) return true;
      const children = groups.filter(g => g.parent_group_id === root.id);
      return children.some(child => selectedIds.has(child.id));
    });
  }, [groups, analyticsFilters?.groupIds]);

  // Compute union date range across all selected root groups (earliest start → latest end)
  const groupDateRange = useMemo(() => {
    if (selectedRootGroups.length === 0) return null;
    try {
      const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };
      let earliestStart: Date | null = null;
      let latestEnd: Date | null = null;
      selectedRootGroups.forEach(g => {
        if (g.start_date) {
          const start = parseLocalDate(g.start_date);
          if (!earliestStart || start < earliestStart) earliestStart = start;
        }
        if (g.end_date) {
          const end = parseLocalDate(g.end_date);
          if (!latestEnd || end > latestEnd) latestEnd = end;
        }
      });
      if (!earliestStart || !latestEnd) return null;
      return `${format(earliestStart, 'MMM d')} → ${format(latestEnd, 'MMM d, yyyy')}`;
    } catch {
      return null;
    }
  }, [selectedRootGroups]);

  // Show group dates checkbox when:
  // - Film industry is selected (either via groups or header filter)
  // - Callback is available
  // HIDE when: groups are selected but ALL are non-film industry
  const showProductionDatesCheckbox = isFilmIndustry && analyticsFilters && onAnalyticsFiltersChange;

  // Disable when: no valid date range available from selected groups
  const groupDatesDisabled = !groupDateRange;

  // Auto-disable useFullGroup when conditions no longer valid (e.g., multiple groups selected)
  useEffect(() => {
    if (groupDatesDisabled && analyticsFilters?.useFullGroup && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        useFullGroup: false,
      });
    }
  }, [groupDatesDisabled, analyticsFilters, onAnalyticsFiltersChange]);

  // Auto-dismiss onboarding tooltip after 60 seconds
  useEffect(() => {
    if (!showOnboarding || !onDismissOnboarding) return;
    const timer = setTimeout(() => {
      onDismissOnboarding();
    }, 60000);
    return () => clearTimeout(timer);
  }, [showOnboarding, onDismissOnboarding]);

  const handleGroupsChange = (groupIds: string[]) => {
    // Dismiss onboarding when user selects a group
    if (groupIds.length > 0 && showOnboarding && onDismissOnboarding) {
      onDismissOnboarding();
    }
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        groupIds,
      });
    }
  };

  const handleGranularityChange = (granularity: TimeGranularity) => {
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        granularity,
      });
    }
  };

  const handlePeriodCountChange = (periodCount: number) => {
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        periodCount,
      });
    }
  };

  const handleUseFullProductionChange = (useFullGroup: boolean) => {
    trackReportProductionDatesToggled(useFullGroup);
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        useFullGroup,
      });
    }
  };

  const handleMetricsChange = (enabledMetrics: MetricKey[]) => {
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        enabledMetrics,
      });
    }
  };

  const handleModelsChange = (modelCodes: string[]) => {
    if (analyticsFilters && onAnalyticsFiltersChange) {
      onAnalyticsFiltersChange({
        ...analyticsFilters,
        modelCodes,
      });
    }
  };

  const handleDatePresetChange = (datePreset: DatePreset) => {
    onFiltersChange({
      ...filters,
      datePreset,
      customDateRange: { start: null, end: null },
    });
  };

  const handleCustomDateRangeChange = (start: Date | null, end: Date | null) => {
    // When dates are provided, set to 'custom' preset
    // When dates are cleared (reset), also set to 'custom' to show "Choose date range"
    onFiltersChange({
      ...filters,
      datePreset: 'custom',
      customDateRange: { start, end },
    });
  };

  const viewMode = analyticsFilters?.viewMode || 'charts';

  return (
    <div className={cn("w-full lg:w-72 flex flex-col gap-4", stretchToFill && "h-full")}>
      {/* Main Filters Card */}
      <div className="bg-card border border-border rounded-lg p-4 overflow-visible">
        {/* Header - Simplified */}
        <div className="flex items-center justify-between pb-3 border-b border-border mb-4 gap-2">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="h-4 w-4 text-[#8FB560] shrink-0" />
            <span className="truncate">Filters</span>
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            disabled={!hasActiveFilters}
            className={cn(
              "text-xs h-7 px-2 transition-colors uppercase",
              hasActiveFilters
                ? 'text-muted-foreground hover:text-foreground'
                : 'text-muted-foreground/40'
            )}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>

        {/* Group/Production Section - Using main CompanyGroupFilters in sidebar mode */}
        {analyticsFilters && (
          <div className="relative overflow-visible">
            <div
              className={cn(
                "rounded-md transition-all duration-300",
                showOnboarding && [
                  "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-card",
                  "shadow-[0_0_12px_rgba(143,181,96,0.4)]"
                ]
              )}
            >
              <CompanyGroupFilters
                key={`group-filter-${mainFilterIndustryTypes.join(',')}`}
                sidebarMode
                selectedGroupIds={analyticsFilters.groupIds}
                onGroupsChange={handleGroupsChange}
                selectedIndustryTypes={mainFilterIndustryTypes}
              />
            </div>
            {/* Onboarding tooltip - above on mobile, to the right on desktop */}
            {showOnboarding && (
              <div className="absolute z-50 animate-in fade-in duration-300 bottom-full left-1/2 -translate-x-1/2 mb-2 slide-in-from-bottom-2 lg:bottom-auto lg:left-full lg:top-1/2 lg:translate-x-0 lg:-translate-y-1/2 lg:mb-0 lg:ml-3 lg:slide-in-from-left-2">
                <div className="relative bg-[#8FB560] rounded-md shadow-lg px-3 py-2 whitespace-nowrap">
                  {/* Arrow pointing down on mobile */}
                  <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#8FB560]" />
                  {/* Arrow pointing left on desktop */}
                  <div className="hidden lg:block absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-[#8FB560]" />
                  <span className="text-sm font-semibold text-black">START HERE</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Production Dates Checkbox - only for film industry */}
        {/* More muted when no groups selected, normal muted when disabled */}
        {showProductionDatesCheckbox && (
          <label
            htmlFor="useProductionDates"
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border-2 transition-all duration-150 mt-3",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-[#8FB560]/50 focus-within:ring-offset-1 focus-within:ring-offset-background",
              // Extra muted when no groups selected at all
              analyticsFilters.groupIds.length === 0 && "opacity-40 cursor-not-allowed",
              // Normal muted when disabled (no dates available)
              analyticsFilters.groupIds.length > 0 && groupDatesDisabled && "opacity-50 cursor-not-allowed",
              !groupDatesDisabled && analyticsFilters.groupIds.length > 0 && "cursor-pointer",
              analyticsFilters.useFullGroup
                ? "bg-[#8FB560]/20 border-[#8FB560] hover:bg-[#8FB560]/25"
                : "bg-[#8FB560]/10 border-[#8FB560]/60 hover:bg-[#8FB560]/15 hover:border-[#8FB560]"
            )}
          >
            <Checkbox
              id="useProductionDates"
              checked={analyticsFilters.useFullGroup}
              onCheckedChange={(checked) => handleUseFullProductionChange(checked === true)}
              disabled={groupDatesDisabled || analyticsFilters.groupIds.length === 0}
              className={cn(
                "h-6 w-6 rounded-md shrink-0",
                "data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]",
                "border-2 border-[#8FB560]/60"
              )}
            />
            <div className="flex-1 text-left min-w-0">
              <span className="text-xs text-muted-foreground uppercase tracking-wider block truncate">Use Production Dates</span>
              {groupDateRange ? (
                <span className={cn(
                  "block text-xs font-medium mt-0.5 uppercase tracking-wider truncate",
                  analyticsFilters.useFullGroup ? "text-[#8FB560]" : "text-foreground/80"
                )}>{groupDateRange}</span>
              ) : (
                <span className="block text-xs text-muted-foreground/60 mt-0.5 uppercase tracking-wider truncate">Select a group first</span>
              )}
            </div>
          </label>
        )}

        <div className="border-t border-border my-4" />

        {/* Time Period Section - Flat, no collapsible */}
        {analyticsFilters && (
          <TimePeriodSelector
            granularity={analyticsFilters.granularity}
            periodCount={analyticsFilters.periodCount}
            useFullGroup={analyticsFilters.useFullGroup}
            onGranularityChange={handleGranularityChange}
            onPeriodCountChange={handlePeriodCountChange}
            onUseFullProductionChange={handleUseFullProductionChange}
            datePreset={filters.datePreset}
            customStartDate={filters.customDateRange.start}
            customEndDate={filters.customDateRange.end}
            onDatePresetChange={handleDatePresetChange}
            onCustomDateRangeChange={handleCustomDateRangeChange}
          />
        )}

        {/* Chart Metrics - Chip selector (only in charts mode) */}
        {analyticsFilters && viewMode === 'charts' && (
          <>
            <div className="border-t border-border my-4" />
            <MetricChipSelector
              enabledMetrics={analyticsFilters.enabledMetrics}
              onMetricsChange={handleMetricsChange}
            />
          </>
        )}

        {/* Unit Models */}
        {analyticsFilters && (
          <>
            <div className="border-t border-border my-4" />
            <div className="relative overflow-visible">
              <div
                className={cn(
                  "rounded-md transition-all duration-300",
                  // Highlight when groups are selected but no models are selected
                  analyticsFilters.groupIds.length > 0 && analyticsFilters.modelCodes.length === 0 && [
                    "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-card",
                    "shadow-[0_0_12px_rgba(143,181,96,0.4)]"
                  ]
                )}
              >
                <UnitModelFilter
                  sidebarMode
                  selectedModelCodes={analyticsFilters.modelCodes}
                  onModelsChange={handleModelsChange}
                  selectedIndustryTypes={mainFilterIndustryTypes}
                  selectedGroupIds={analyticsFilters.groupIds}
                  modelCounts={summary?.modelCounts}
                />
              </div>
              {/* Model required tooltip - above on mobile, to the right on desktop */}
              {analyticsFilters.groupIds.length > 0 && analyticsFilters.modelCodes.length === 0 && (
                <div className="absolute z-50 animate-in fade-in duration-300 bottom-full left-1/2 -translate-x-1/2 mb-2 slide-in-from-bottom-2 lg:bottom-auto lg:left-full lg:top-1/2 lg:translate-x-0 lg:-translate-y-1/2 lg:mb-0 lg:ml-3 lg:slide-in-from-left-2">
                  <div className="relative bg-[#8FB560] rounded-md shadow-lg px-3 py-2 whitespace-nowrap">
                    {/* Arrow pointing down on mobile */}
                    <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#8FB560]" />
                    {/* Arrow pointing left on desktop */}
                    <div className="hidden lg:block absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-[#8FB560]" />
                    <span className="text-sm font-semibold text-black">MODEL REQUIRED</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Unit Status Card */}
      <StatusBreakdown
        summary={analyticsFilters && analyticsFilters.groupIds.length > 0 ? summary : undefined}
        selectedStatus={focusedStatus}
        onSelectStatus={onToggleStatusFocus}
        onClear={onClearStatusFocus}
      />

      {/* Units by Model Card - flex-1 when stretching to fill empty state */}
      <ModelBreakdown
        modelCounts={analyticsFilters && analyticsFilters.groupIds.length > 0 ? summary?.modelCounts : undefined}
        totalUnits={analyticsFilters && analyticsFilters.groupIds.length > 0 ? summary?.totalUnits : 0}
        className={stretchToFill ? "flex-1" : undefined}
        selectedModel={focusedModel}
        onSelectModel={onToggleModelFocus}
        onClear={onClearModelFocus}
      />
    </div>
  );
}
